<template>
  <div
    class="rater-card"
    @click="handleClick"
  >
    <div class="avatar-shell">
      <img
        :src="rater?.imageURL || defaultAvatar"
        alt="Аватар"
        class="rater-avatar"
        @error="handleAvatarError"
      />
    </div>

    <div class="rater-content">
      <p class="rater-name">
        {{ rater?.name || "Аноним" }}
      </p>

      <p class="rater-age">
        {{ rater?.age ? `${rater.age} лет` : "Возраст не указан" }}
      </p>

      <p class="rater-specific">
        {{ rater?.specifics || "Специфика не указана" }}
      </p>
    </div>
  </div>
</template>
<script setup>
import Rater from "../models/Rater.js";
import defaultAvatar from "../assets/default-avatar.png";

const emit =
  defineEmits([
    "click"
  ]);

const props =
  defineProps({
    rater: {
      type: Rater,
      required: true
    }
  });

function handleClick() {
  emit(
    "click",
    props.rater.id
  );
}

function handleAvatarError(event) {
  event.target.src = defaultAvatar;
}
</script>

<style scoped>

/* ============================================================
   CARD
============================================================ */

.rater-card {
  width: 150px;
  height: 210px;

  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding:
    16px
    12px
    14px;

  overflow: hidden;

  text-align: center;

  background:
    #ffffff;

  border:
    1px solid
    rgba(20, 35, 55, 0.07);

  border-radius: 18px;

  box-shadow:
    0
    10px
    28px
    rgba(15, 23, 42, 0.04);

  cursor: pointer;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.rater-card:hover {
  transform:
    translateY(-2px);

  border-color:
    rgba(70, 105, 155, 0.12);

  box-shadow:
    0
    15px
    32px
    rgba(15, 23, 42, 0.07);
}


.rater-card:active {
  transform:
    translateY(0)
    scale(0.985);
}


/* ============================================================
   AVATAR
============================================================ */

.avatar-shell {
  width: 88px;
  height: 88px;

  flex:
    0
    0
    88px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding: 3px;

  background:
    linear-gradient(
      145deg,
      #f4f6f9,
      #eceff3
    );

  border:
    1px solid
    rgba(20, 35, 55, 0.055);

  border-radius: 50%;

  box-shadow:
    0
    8px
    20px
    rgba(15, 23, 42, 0.08);
}


.rater-avatar {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 50%;
}


/* ============================================================
   CONTENT
============================================================ */

.rater-content {
  width: 100%;

  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  margin-top: 10px;
}


/* ============================================================
   NAME
============================================================ */

.rater-name {
  width: 100%;

  margin: 0;

  overflow: hidden;

  color: #30353c;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;

  line-height: 1.25;

  white-space: nowrap;

  text-overflow: ellipsis;
}


/* ============================================================
   AGE
============================================================ */

.rater-age {
  margin:
    4px
    0
    0;

  color: #969ba4;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;
}


/* ============================================================
   SPECIFIC
============================================================ */

.rater-specific {
  width: 100%;
  max-width: 126px;

  margin:
    8px
    0
    0;

  padding:
    5px
    8px;

  box-sizing: border-box;

  color: #667f9f;

  background: #f3f6fa;

  border:
    1px solid
    rgba(65, 100, 155, 0.065);

  border-radius: 8px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
  line-height: 1.3;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;

  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.rater-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    14px
    32px
    rgba(0, 0, 0, 0.16);
}


html[data-theme="dark"]
.rater-card:hover {
  border-color:
    rgba(255, 255, 255, 0.10);

  box-shadow:
    0
    18px
    36px
    rgba(0, 0, 0, 0.24);
}


html[data-theme="dark"]
.avatar-shell {
  background:
    linear-gradient(
      145deg,
      #2a2d33,
      #25272c
    );

  border-color:
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0
    8px
    22px
    rgba(0, 0, 0, 0.30);
}


html[data-theme="dark"]
.rater-name {
  color: #f0f1f3;
}


html[data-theme="dark"]
.rater-age {
  color: #747981;
}


html[data-theme="dark"]
.rater-specific {
  color: #a4b2c4;

  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.055);
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .rater-card {
    transition: none;
  }

}

html[data-theme="dark"] .heading-icon {
  border:
    1px solid
    rgba(255, 255, 255, 0.055);
}


/* OFFICE */

html[data-theme="dark"] .office-icon {
  color: #8faacb;

  background:
    rgba(115, 145, 185, 0.09);
}


/* SOCIAL */

html[data-theme="dark"] .social-heading-icon {
  color: #aaa0c7;

  background:
    rgba(135, 115, 175, 0.09);
}


/* PEOPLE */

html[data-theme="dark"] .people-icon {
  color: #8eb8a5;

  background:
    rgba(85, 145, 115, 0.09);
}


/* REVIEWS */

html[data-theme="dark"] .review-icon {
  color: #c5aa7b;

  background:
    rgba(175, 135, 75, 0.09);
}


</style>